import moment from 'moment';

/**
 * Map a FULL_SCREEN_POPUP announcement (from GET_FULL_SCREEN_ANNOUNCEMENT) back
 * into the flat form values the wizard uses. The inverse of buildFullScreenData.
 *
 * Note: targetAudience.userIds comes back as bare ids; the wizard separately
 * resolves them to the "name;...;id;email" display format used by the picker.
 */
export const mapFullScreenToForm = announcement => {
  if (!announcement) return {};

  const ta = announcement.targetAudience || {};
  const {signUpTime, lastAppVisit, transaction, kycStatus} = ta;
  const tx = transaction || {};
  const popupConfig = announcement.popupConfig || {};
  const renewal = popupConfig.subscriptionRenewal;
  // The original language lives on the announcement; multiLang holds translations.
  const translationEntry = (announcement.multiLang || []).find(
    entry => entry?.language && entry.language !== announcement.language,
  );

  return {
    // settings
    language: announcement.language,
    type: announcement.type,
    purpose: popupConfig.purpose,
    openUrlOnClick: announcement.openUrlOnClick,
    publishedDate: announcement.publishedDate
      ? moment(announcement.publishedDate)
      : null,
    expiredDate: announcement.expiredDate
      ? moment(announcement.expiredDate)
      : null,
    report: announcement.report?.id,
    opportunity: announcement.opportunity?.id,
    company: announcement.company?.id,
    instrument: announcement.instrument?.id,
    collection: announcement.collection?.id,

    // content
    title: announcement.title,
    subTitle: announcement.subTitle,
    imageUrl: announcement.imageUrl,
    announcementIcon: announcement.announcementIcon,
    popUpContent: announcement.popUpContent,
    headerBadgeText: announcement.headerBadgeText,
    footerText: announcement.footerText,
    closeButtonLabel: announcement.closeButtonLabel,
    actionButtonLabel: announcement.actionButtonLabel,
    deeplinkUrl: announcement.deeplinkUrl,
    dismissible: popupConfig.dismissible,
    maxShowCount: popupConfig.maxShowCount,
    benefits: (announcement.benefits || []).map(benefit => ({
      label: benefit.label,
      icon: benefit.icon,
    })),

    ...(renewal && {
      subscriptionRenewal: {
        planKey: renewal.planKey,
        catalogTabs: (renewal.catalogTabs || []).map(tab => ({
          id: tab.id,
          catalogKey: tab.catalogKey,
          isDefault: !!tab.isDefault,
          applyDiscount: !!(tab.appliedDiscount && tab.appliedDiscount.length),
          discounts: (tab.appliedDiscount || []).map(discount => ({
            type: discount.type || 'FIXED',
            value: discount.value ?? null,
            currency: discount.currency,
          })),
        })),
      },
    }),

    ...(translationEntry && {
      translation: translationEntry.language,
      multilang: {
        title: translationEntry.title,
        subTitle: translationEntry.subTitle,
        headerBadgeText: translationEntry.headerBadgeText,
        footerText: translationEntry.footerText,
        closeButtonLabel: translationEntry.closeButtonLabel,
        actionButtonLabel: translationEntry.actionButtonLabel,
        popUpContent: translationEntry.popUpContent,
        benefits: (translationEntry.benefits || []).map(benefit => ({
          label: benefit.label,
        })),
      },
    }),

    // target audience
    userIds: ta.userIds || undefined,
    userSegments: ta.userSegments,
    riskProfiles: ta.riskProfiles,
    countryCodes: ta.countryCodes,
    countriesOfInterest: ta.countriesOfInterest,
    identityCheck: kycStatus?.identityCheck,
    documentCheck: kycStatus?.documentCheck,
    amlCheck: kycStatus?.amlCheck,
    signupStartDate: signUpTime?.start ? moment(signUpTime.start) : null,
    signupEndDate: signUpTime?.end ? moment(signUpTime.end) : null,
    lastAppVisitedStartDate: lastAppVisit?.start
      ? moment(lastAppVisit.start)
      : null,
    lastAppVisitedEndDate: lastAppVisit?.end ? moment(lastAppVisit.end) : null,
    investmentStartDate: tx.startDate ? moment(tx.startDate) : null,
    investmentEndDate: tx.endDate ? moment(tx.endDate) : null,
    directions: tx.directions,
    statuses: tx.statuses,
    instrumentKeys: tx.instrumentKeys,
  };
};
